import { checkUrlParam } from "../../SharedModule/utils/headerUtils";
import { isStringDateValid } from "../../SharedModule/utils/dateUtils";

// ---------------------------------------------------------------------------
// computeQueryPageFilters — TimesQueryPage's setLocalFilters (ASUITE11-4412,
// S3776). A 1:1 translation of the original logic; setInitialUserInFilters
// is called by the caller beforehand (already a separate, unflagged pure
// function) and its result passed in as context.initialUser, and the
// setPageFilters(...) call stays in the component.
// ---------------------------------------------------------------------------

export type QueryPageFiltersContext = {
  filters: any;
  searchParams: { get(key: string): string | null };
  metaTaskTypesInCache: string | null;
  taskTypesMeta: any[] | undefined;
  initialUser: any;
};

// ASUITE11-4412, S3776 follow-up: a local Sonar run after the original
// extraction still flagged this function (complexity 19) — moving the logic
// out of the component didn't reduce its own control-flow count. The
// dateFromUrlOrFallback/boolFromUrlOrFallback helpers below replace five
// structurally-identical ternary+&&-chain pairs (same "checkUrlParam(raw) &&
// <validity check> ? <parsed> : <fallback>" shape) with one call each, and
// the taskType if/else-if moves to its own function.
const computeTaskTypeFilter = (
  context: Pick<
    QueryPageFiltersContext,
    "filters" | "searchParams" | "metaTaskTypesInCache" | "taskTypesMeta"
  >,
) => {
  if (
    checkUrlParam(context.searchParams.get("taskType")) &&
    context.metaTaskTypesInCache
  ) {
    return [
      JSON.parse(context.metaTaskTypesInCache).find(
        (elem) => elem.label === context.searchParams.get("taskType"),
      ),
    ];
  }
  if (context.taskTypesMeta?.length === 1) {
    return [context.taskTypesMeta[0]];
  }
  return context.filters.taskType;
};

const dateFromUrlOrFallback = (
  searchParams: QueryPageFiltersContext["searchParams"],
  paramName: string,
  fallback: any,
) => {
  const raw = searchParams.get(paramName);
  return checkUrlParam(raw) && isStringDateValid(raw + "T00:00:00.000Z")
    ? raw + "T00:00:00.000Z"
    : fallback;
};

const boolFromUrlOrFallback = (
  searchParams: QueryPageFiltersContext["searchParams"],
  paramName: string,
  fallback: any,
) => {
  const raw = searchParams.get(paramName);
  return checkUrlParam(raw) && !isNaN(Number(raw)) ? !!Number(raw) : fallback;
};

export const computeQueryPageFilters = (
  structureData: any,
  context: QueryPageFiltersContext,
) => ({
  startDate: dateFromUrlOrFallback(
    context.searchParams,
    "dateFrom",
    context.filters.startDate,
  ),
  endDate: dateFromUrlOrFallback(
    context.searchParams,
    "dateTo",
    context.filters.endDate,
  ),
  costCenter:
    structureData.costCentersQuery?.length === 1
      ? [structureData.costCentersQuery[0]]
      : context.filters.costCenter,
  client:
    structureData.clientsQuery?.length === 1
      ? [structureData.clientsQuery[0]]
      : context.filters.client,
  project: checkUrlParam(context.searchParams.get("projectId"))
    ? structureData.projectsQuery && [
        structureData.projectsQuery.find(
          (elem) =>
            elem.entityId === Number(context.searchParams.get("projectId")),
        ),
      ]
    : context.filters.project,
  projectManager:
    structureData.projectManagersQuery?.length === 1
      ? [structureData.projectManagersQuery[0]]
      : context.filters.projectManager,
  user: context.initialUser,
  // aditional filters
  includeInactiveUsers: context.filters.includeInactiveUsers,
  taskType: computeTaskTypeFilter(context),
  country:
    structureData.countriesQuery?.length === 1
      ? [structureData.countriesQuery[0]]
      : context.filters.country,
  hourType: {
    billableHours: boolFromUrlOrFallback(
      context.searchParams,
      "viewBillable",
      context.filters.hourType.billableHours,
    ),
    nonBillableHours: boolFromUrlOrFallback(
      context.searchParams,
      "viewNonBillable",
      context.filters.hourType.nonBillableHours,
    ),
    internalHours: context.filters.hourType.internalHours,
    financedHours: boolFromUrlOrFallback(
      context.searchParams,
      "viewFinanced",
      context.filters.hourType.financedHours,
    ),
  },
  nonBillableReasons: context.filters.nonBillableReasons,
  timeOff: {
    paidTimeOff: context.filters.timeOff.paidTimeOff,
    unpaidTimeOff: context.filters.timeOff.unpaidTimeOff,
  },
  approvalStatus: {
    pendingHours: context.filters.approvalStatus.pendingHours,
    approvedHours: context.filters.approvalStatus.approvedHours,
    lockedHours: context.filters.approvalStatus.lockedHours,
  },
  summary: {
    byUser: context.filters.summary.byUser,
    byTask: context.filters.summary.byTask,
    byTaskType: context.filters.summary.byTaskType,
    hideEntries: context.filters.summary.hideEntries,
  },
});
